import { ensureOk } from "@/api/apiError";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { Session, SessionUpdate } from "@/types";

const SESSIONS_QUERY_KEY = ["sessions"];

async function fetchSessions(): Promise<Session[]> {
  const response = await httpClient("/sessions");
  await ensureOk(response);
  return response.json() as Promise<Session[]>;
}

async function createSession(agentId: string): Promise<Session> {
  const response = await httpClient("/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ agentId }),
  });
  await ensureOk(response);
  return response.json() as Promise<Session>;
}

async function updateSession(
  sessionId: string,
  payload: SessionUpdate,
): Promise<Session> {
  const response = await httpClient(
    `/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  await ensureOk(response);
  return response.json() as Promise<Session>;
}

async function transitionSession(
  sessionId: string,
  action: "close" | "reopen",
): Promise<Session> {
  const response = await httpClient(
    `/sessions/${encodeURIComponent(sessionId)}/${action}`,
    { method: "POST" },
  );
  await ensureOk(response);
  return response.json() as Promise<Session>;
}

async function deleteSession(sessionId: string): Promise<void> {
  const response = await httpClient(
    `/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "DELETE",
    },
  );
  await ensureOk(response);
}

/** Sessions list — pinned first, then by last activity (server-sorted). */
export function useSessions() {
  return useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: fetchSessions,
    staleTime: 30 * 1000,
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSession,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      payload,
    }: {
      sessionId: string;
      payload: SessionUpdate;
    }) => updateSession(sessionId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useCloseSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => transitionSession(sessionId, "close"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useReopenSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => transitionSession(sessionId, "reopen"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSession,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}
